import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth header to all requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me')
};

// Customers API
export const customersAPI = {
    list: (search = '') => api.get('/customers', { params: { search } }),
    get: (id) => api.get(`/customers/${id}`),
    create: (data) => api.post('/customers', data),
    update: (id, data) => api.put(`/customers/${id}`, data),
    delete: (id) => api.delete(`/customers/${id}`)
};

// Jobs API
export const jobsAPI = {
    list: (params = {}) => api.get('/jobs', { params }),
    get: (id) => api.get(`/jobs/${id}`),
    create: (data) => api.post('/jobs', data),
    update: (id, data) => api.put(`/jobs/${id}`, data),
    delete: (id) => api.delete(`/jobs/${id}`),
    // Rooms (areas)
    addRoom: (jobId, data) => api.post(`/jobs/${jobId}/areas`, data),
    updateRoom: (jobId, roomId, data) => api.put(`/jobs/${jobId}/areas/${roomId}`, data),
    deleteRoom: (jobId, roomId) => api.delete(`/jobs/${jobId}/areas/${roomId}`),
    // Legacy area methods (kept for compat)
    addArea: (jobId, data) => api.post(`/jobs/${jobId}/areas`, data),
    updateArea: (jobId, areaId, data) => api.put(`/jobs/${jobId}/areas/${areaId}`, data),
    deleteArea: (jobId, areaId) => api.delete(`/jobs/${jobId}/areas/${areaId}`),
    // Manual Adjustments (internal, hidden from quote)
    addAdjustment: (jobId, data) => api.post(`/jobs/${jobId}/adjustments`, data),
    deleteAdjustment: (jobId, adjustmentId) => api.delete(`/jobs/${jobId}/adjustments/${adjustmentId}`),
    // Manual Entry for Quote (shown on quote)
    addAdditionalWork: (jobId, data) => api.post(`/jobs/${jobId}/additional-works`, data),
    deleteAdditionalWork: (jobId, workId) => api.delete(`/jobs/${jobId}/additional-works/${workId}`),
    // Photos
    addPhoto: (jobId, photo) => api.post(`/jobs/${jobId}/photos/upload`, { photo }),
    // PDF
    getQuotePDF: (jobId) => api.get(`/jobs/${jobId}/quote-pdf`, { responseType: 'blob' }),
    getInvoicePDF: (jobId) => api.get(`/jobs/${jobId}/invoice-pdf`, { responseType: 'blob' }),
    // Quote line overrides (manual edit of quote lines)
    saveQuoteOverrides: (jobId, lines) => api.put(`/jobs/${jobId}/quote-overrides`, { overrides: lines }),
    // Invoice management
    createInvoice: (jobId, lineItems) => api.post(`/jobs/${jobId}/invoice`, { line_items: lineItems }),
    updateInvoiceStatus: (jobId, status) => api.put(`/jobs/${jobId}/invoice/status`, null, { params: { status } })
};

// Rates API
export const ratesAPI = {
    get: () => api.get('/rates'),
    update: (data) => api.put('/rates', data),
    export: () => api.get('/rates/export', { responseType: 'blob' }),
    import: (csvContent) => api.post('/rates/import', { csv_content: csvContent }),
    getVersions: () => api.get('/rates/versions')
};

// Presets API
export const presetsAPI = {
    list: () => api.get('/presets'),
    create: (data) => api.post('/presets', data),
    update: (id, data) => api.put(`/presets/${id}`, data),
    delete: (id) => api.delete(`/presets/${id}`),
    export: () => api.get('/presets/export', { responseType: 'blob' }),
    import: (csvContent) => api.post('/presets/import', { csv_content: csvContent })
};

// Quote Template API
export const templateAPI = {
    get: () => api.get('/quote-template'),
    update: (data) => api.put('/quote-template', data)
};

// Invoice Template API
export const invoiceTemplateAPI = {
    get: () => api.get('/invoice-template'),
    update: (data) => api.put('/invoice-template', data)
};

// Dashboard API
export const dashboardAPI = {
    getStats: (params = {}) => api.get('/dashboard/stats', { params })
};

// Export API
export const exportAPI = {
    customers: () => api.get('/export/customers', { responseType: 'blob' }),
    jobs: () => api.get('/export/jobs', { responseType: 'blob' })
};

// Settings API (alias for quote template, used for review message)
export const settingsAPI = {
    getTemplate: () => api.get('/quote-template'),
    updateTemplate: (data) => api.put('/quote-template', data)
};

// Admin API
export const adminAPI = {
    downloadBackup: () => api.get('/admin/backup', { responseType: 'blob' })
};

// Materials & Compositions API
export const materialsAPI = {
    listMaterials: () => api.get('/materials'),
    createMaterial: (data) => api.post('/materials', data),
    updateMaterial: (id, data) => api.put(`/materials/${id}`, data),
    deleteMaterial: (id) => api.delete(`/materials/${id}`),
    listCompositions: () => api.get('/compositions'),
    upsertComposition: (rateKey, data) => api.put(`/compositions/${rateKey}`, data),
    recalculate: () => api.post('/compositions/recalculate'),
    getJobEstimates: (jobId) => api.get(`/jobs/${jobId}/material-estimates`)
};

// Custom Work Types API
export const customWorkTypesAPI = {
    list: () => api.get('/custom-work-types'),
    create: (data) => api.post('/custom-work-types', data),
    update: (id, data) => api.put(`/custom-work-types/${id}`, data),
    delete: (id) => api.delete(`/custom-work-types/${id}`)
};

// Job Extras Presets API
export const extrasAPI = {
    listPresets: () => api.get('/extras-presets'),
    createPreset: (data) => api.post('/extras-presets', data),
    updatePreset: (id, data) => api.put(`/extras-presets/${id}`, data),
    deletePreset: (id) => api.delete(`/extras-presets/${id}`),
    saveJobExtras: (jobId, extras) => api.put(`/jobs/${jobId}/extras`, { extras })
};

// Users Admin API
export const usersAPI = {
    list: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`)
};

// Seed API
export const seedAPI = {
    seed: () => api.post('/seed')
};

export default api;
